import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { getRoleCapabilities } from "../../config/permissions";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import api from "../../api/client";

const phoneRegExp =
  /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;

const optionalEmail = yup
  .string()
  .trim()
  .test("email", "invalid email", (v) => !v || yup.string().email().isValidSync(v));

const optionalUrl = yup
  .string()
  .trim()
  .test("url", "invalid url", (v) => !v || yup.string().url().isValidSync(v));

const optionalPhone = yup
  .string()
  .trim()
  .test("phone", "invalid phone", (v) => !v || phoneRegExp.test(v));

const checkoutSchema = yup.object({
  clientName: yup.string().trim(),
  brandName: yup.string().trim(),
  brandLogo: yup.string(),
  primaryColor: yup.string(),
  secondaryColor: yup.string(),
  accentColor: yup.string(),
  website: optionalUrl,
  email: optionalEmail,
  contactNumber: optionalPhone,
  contactPerson: yup.string().trim(),
  productDescription: yup.string().trim(),
  address: yup.string().trim(),
  city: yup.string().trim(),
  zipCode: yup.string().trim(),
});

const emptyForm = {
  clientName: "",
  brandName: "",
  brandLogo: "",
  brandLogoName: "",
  primaryColor: "#0B3D91",
  secondaryColor: "#00A8E8",
  accentColor: "#EAF6FF",
  website: "",
  email: "",
  contactNumber: "",
  contactPerson: "",
  productDescription: "",
  address: "",
  city: "",
  zipCode: "",
};

const Form = () => {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const caps = useMemo(() => getRoleCapabilities(user?.role), [user?.role]);
  const isNonMobile = useMediaQuery("(min-width:600px)");
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [clients, setClients] = useState([]);
  const [designers, setDesigners] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [formInitial, setFormInitial] = useState(emptyForm);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transfer, setTransfer] = useState({ fromUserId: "", toUserId: "", brandIds: [] });

  const loadBrands = useCallback(async () => {
    setLoadingList(true);
    try {
      const { data } = await api.get("/api/brands");
      setClients(
        (data || []).map((b) => ({
          id: b.id,
          clientName: b.clientName || "",
          brandName: b.brandName || "",
          logoUrl: b.logoUrl,
          assignedDesignerId: b.assignedDesignerId || "",
          assignedDesignerName: b.assignedDesignerName || "",
        }))
      );
    } catch {
      setClients([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadBrands();
  }, [loadBrands]);

  const loadDesigners = useCallback(async () => {
    if (!caps.canAssignBrands && !(caps.isManager && caps.canTransferBrandsBetweenDesigners)) return;
    try {
      const { data } = await api.get("/api/users/designers");
      setDesigners(data || []);
    } catch {
      setDesigners([]);
    }
  }, [caps.canAssignBrands, caps.canTransferBrandsBetweenDesigners, caps.isManager]);

  useEffect(() => {
    loadDesigners();
  }, [loadDesigners]);

  useEffect(() => {
    if (user?.role === "designer") {
      navigate("/brands", { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!brandId) {
      setFormInitial(emptyForm);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/api/brands/${brandId}`);
        setFormInitial({
          clientName: data.clientName || "",
          brandName: data.brandName || "",
          brandLogo: "",
          brandLogoName: "",
          primaryColor: data.primaryColor || "#0B3D91",
          secondaryColor: data.secondaryColor || "#00A8E8",
          accentColor: data.accentColor || "#EAF6FF",
          website: data.website || "",
          email: data.email || "",
          contactNumber: data.contactNumber || "",
          contactPerson: data.contactPerson || "",
          productDescription: data.productDescription || "",
          address: data.address || "",
          city: data.city || "",
          zipCode: data.zipCode || "",
        });
      } catch (e) {
        console.error(e);
      }
    })();
  }, [brandId]);

  const handleFormSubmit = async (values, { resetForm }) => {
    const payload = {
      clientName: values.clientName,
      brandName: values.brandName,
      primaryColor: values.primaryColor,
      secondaryColor: values.secondaryColor,
      accentColor: values.accentColor,
      website: values.website,
      email: values.email,
      contactNumber: values.contactNumber,
      contactPerson: values.contactPerson,
      productDescription: values.productDescription,
      address: values.address,
      city: values.city,
      zipCode: values.zipCode,
      brandLogo: values.brandLogo || undefined,
    };
    if (brandId) {
      await api.patch(`/api/brands/${brandId}`, payload);
    } else {
      await api.post("/api/brands", payload);
    }
    await loadBrands();
    resetForm({ values: { ...emptyForm } });
    setFormInitial(emptyForm);
    if (brandId) {
      if (user?.role === "admin") navigate("/brands");
      else navigate("/form");
    }
  };

  const handleLogoUpload = (event, setFieldValue) => {
    const file = event.currentTarget.files && event.currentTarget.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setFieldValue("brandLogo", reader.result);
      setFieldValue("brandLogoName", file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleAssignDesigner = async (brandId, designerId) => {
    try {
      await api.patch(`/api/brands/${brandId}/assign`, {
        assignedDesignerId: designerId || null,
      });
      await loadBrands();
    } catch (e) {
      console.error(e);
    }
  };

  const submitTransfer = async () => {
    await api.post("/api/users/transfer-brands", {
      fromUserId: transfer.fromUserId,
      toUserId: transfer.toUserId,
      brandIds: transfer.brandIds,
    });
    setTransferOpen(false);
    setTransfer({ fromUserId: "", toUserId: "", brandIds: [] });
    await loadBrands();
    await loadDesigners();
  };

  const handleDeleteClient = async (clientId) => {
    if (!caps.canDeleteClients) return;
    const ok = window.confirm("Delete this client?");
    if (!ok) return;
    try {
      await api.delete(`/api/brands/${clientId}`);
      await loadBrands();
      if (brandId && brandId === clientId) {
        navigate("/form", { replace: true });
      }
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.message || "Failed to delete client");
    }
  };

  return (
    <Box m="20px">
      {caps.isManager && caps.canTransferBrandsBetweenDesigners ? (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <Button variant="outlined" color="secondary" onClick={() => setTransferOpen(true)}>
            Transfer brands between designers
          </Button>
        </Box>
      ) : null}
      <Box display="grid" gridTemplateColumns="repeat(12, minmax(0, 1fr))" gap="20px">
        <Box
          gridColumn={isNonMobile ? "span 5" : "span 12"}
          backgroundColor={colors.primary[400]}
          p="20px"
          minHeight="70vh"
        >
          <Typography variant="h4" fontWeight="600" mb="15px">
            Existing Clients
          </Typography>
          {loadingList ? (
            <Typography color={colors.grey[300]}>Loading…</Typography>
          ) : clients.length === 0 ? (
            <Typography color={colors.grey[300]}>
              No brands yet. Save a brand profile to see it listed here.
            </Typography>
          ) : (
            clients.map((client) => (
              <Box
                key={client.id}
                p="16px"
                mb="10px"
                borderRadius="6px"
                backgroundColor={colors.primary[500]}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap="16px"
              >
                <Box display="flex" gap={1.5} alignItems="center" minWidth={0}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      border: `1px solid ${colors.grey[300]}`,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      overflow: "hidden",
                      flexShrink: 0,
                    }}
                  >
                    {client.logoUrl ? (
                      <Box
                        component="img"
                        src={client.logoUrl}
                        alt=""
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : null}
                  </Box>
                  <Box>
                    <Typography fontWeight="600">{client.clientName || "—"}</Typography>
                    <Typography variant="body2" color={colors.grey[300]}>
                      {client.brandName || "Untitled"}
                    </Typography>
                  </Box>
                </Box>
                <Box display="flex" flexDirection="column" gap={1} flexShrink={0} alignItems="stretch" minWidth={160}>
                  <Button
                    size="medium"
                    variant="contained"
                    sx={{
                      backgroundColor: colors.blueAccent[600],
                      color: colors.grey[100],
                      px: 2,
                      "&:hover": { backgroundColor: colors.blueAccent[500] },
                    }}
                    onClick={() => navigate(`/form/${client.id}`)}
                  >
                    Edit Details
                  </Button>
                  {caps.canAssignBrands ? (
                    <FormControl size="small" fullWidth variant="filled" sx={{ backgroundColor: colors.primary[500] }}>
                      <InputLabel id={`designer-label-${client.id}`}>Designer</InputLabel>
                      <Select
                        labelId={`designer-label-${client.id}`}
                        label="Designer"
                        value={client.assignedDesignerId || ""}
                        onChange={(e) => handleAssignDesigner(client.id, e.target.value)}
                      >
                        <MenuItem value="">
                          <em>Unassigned</em>
                        </MenuItem>
                        {designers.map((d) => (
                          <MenuItem key={d.id} value={d.id}>
                            {d.name || d.email}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}
                  {caps.canDeleteClients ? (
                    <Button
                      size="medium"
                      variant="outlined"
                      sx={{
                        color: colors.redAccent[400],
                        borderColor: colors.redAccent[400],
                        px: 2,
                      }}
                      onClick={() => handleDeleteClient(client.id)}
                    >
                      Delete
                    </Button>
                  ) : null}
                </Box>
              </Box>
            ))
          )}
        </Box>

        <Box gridColumn={isNonMobile ? "span 7" : "span 12"}>
          <Header
            title="CLIENT"
            subtitle={brandId ? "Edit client / brand profile" : "Create a New Client Profile"}
          />

          <Formik
            enableReinitialize
            key={brandId || "new"}
            onSubmit={handleFormSubmit}
            initialValues={formInitial}
            validationSchema={checkoutSchema}
          >
            {({
              values,
              errors,
              touched,
              handleBlur,
              handleChange,
              handleSubmit,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <Box
                  display="grid"
                  gap="20px"
                  gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                  sx={{
                    "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
                  }}
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Client Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.clientName}
                    name="clientName"
                    error={!!touched.clientName && !!errors.clientName}
                    helperText={touched.clientName && errors.clientName}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Client Brand Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.brandName}
                    name="brandName"
                    error={!!touched.brandName && !!errors.brandName}
                    helperText={touched.brandName && errors.brandName}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <Box sx={{ gridColumn: "span 2" }}>
                    <Typography variant="body2" color={colors.grey[200]} mb="8px">
                      Client Logo
                    </Typography>
                    <Button variant="contained" component="label" color="secondary">
                      Add Logo
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(event) => handleLogoUpload(event, setFieldValue)}
                      />
                    </Button>
                    <Typography variant="caption" color={colors.grey[300]} display="block" mt="8px">
                      {values.brandLogoName || "No new logo selected"}
                    </Typography>
                    {values.brandLogo ? (
                      <Box
                        component="img"
                        src={values.brandLogo}
                        alt="Client logo preview"
                        sx={{
                          width: "100%",
                          maxWidth: "180px",
                          height: "100px",
                          objectFit: "contain",
                          backgroundColor: colors.primary[500],
                          borderRadius: "4px",
                          mt: "8px",
                          p: "8px",
                        }}
                      />
                    ) : null}
                  </Box>

                  <Box sx={{ gridColumn: "span 2" }}>
                    <Typography variant="body2" color={colors.grey[200]} mb="8px">
                      Client Color Scheme (Eyedropper)
                    </Typography>
                    <Box display="flex" gap="10px" alignItems="center">
                      <input
                        type="color"
                        name="primaryColor"
                        value={values.primaryColor}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        title="Primary color"
                        style={{ width: "56px", height: "38px", cursor: "pointer" }}
                      />
                      <input
                        type="color"
                        name="secondaryColor"
                        value={values.secondaryColor}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        title="Secondary color"
                        style={{ width: "56px", height: "38px", cursor: "pointer" }}
                      />
                      <input
                        type="color"
                        name="accentColor"
                        value={values.accentColor}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        title="Accent color"
                        style={{ width: "56px", height: "38px", cursor: "pointer" }}
                      />
                    </Box>
                    <Box display="flex" mt="10px" borderRadius="4px" overflow="hidden">
                      <Box height="10px" flex={1} sx={{ backgroundColor: values.primaryColor }} />
                      <Box height="10px" flex={1} sx={{ backgroundColor: values.secondaryColor }} />
                      <Box height="10px" flex={1} sx={{ backgroundColor: values.accentColor }} />
                    </Box>
                    <Typography variant="caption" color={colors.grey[300]}>
                      Default mixer is loaded. Use the picker eye-dropper to sample from logo colors.
                    </Typography>
                  </Box>

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Website"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.website}
                    name="website"
                    error={!!touched.website && !!errors.website}
                    helperText={touched.website && errors.website}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Email Id"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.email}
                    name="email"
                    error={!!touched.email && !!errors.email}
                    helperText={touched.email && errors.email}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Contact Number"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.contactNumber}
                    name="contactNumber"
                    error={!!touched.contactNumber && !!errors.contactNumber}
                    helperText={touched.contactNumber && errors.contactNumber}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Contact Person"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.contactPerson}
                    name="contactPerson"
                    error={!!touched.contactPerson && !!errors.contactPerson}
                    helperText={touched.contactPerson && errors.contactPerson}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Product Description"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.productDescription}
                    name="productDescription"
                    error={!!touched.productDescription && !!errors.productDescription}
                    helperText={touched.productDescription && errors.productDescription}
                    multiline
                    rows={4}
                    sx={{ gridColumn: "span 4" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Address"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.address}
                    name="address"
                    error={!!touched.address && !!errors.address}
                    helperText={touched.address && errors.address}
                    sx={{ gridColumn: "span 4" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="City"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.city}
                    name="city"
                    error={!!touched.city && !!errors.city}
                    helperText={touched.city && errors.city}
                    sx={{ gridColumn: "span 2" }}
                  />
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Zip Code"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.zipCode}
                    name="zipCode"
                    error={!!touched.zipCode && !!errors.zipCode}
                    helperText={touched.zipCode && errors.zipCode}
                    sx={{ gridColumn: "span 2" }}
                  />
                </Box>
                <Box display="flex" justifyContent="end" mt="20px">
                  <Button type="submit" color="secondary" variant="contained">
                    {brandId ? "Save Client" : "Create New Client"}
                  </Button>
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      </Box>

      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Transfer brands between designers</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense" variant="filled" sx={{ mt: 1 }}>
            <InputLabel>From designer</InputLabel>
            <Select
              label="From designer"
              value={transfer.fromUserId}
              onChange={(e) => setTransfer({ ...transfer, fromUserId: e.target.value })}
            >
              {designers.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.email} ({r.name || "designer"})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense" variant="filled">
            <InputLabel>To designer</InputLabel>
            <Select
              label="To designer"
              value={transfer.toUserId}
              onChange={(e) => setTransfer({ ...transfer, toUserId: e.target.value })}
            >
              {designers.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.email} ({r.name || "designer"})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
            Brands to move
          </Typography>
          <FormGroup>
            {clients.map((b) => (
              <FormControlLabel
                key={b.id}
                control={
                  <Checkbox
                    checked={transfer.brandIds.map(String).includes(String(b.id))}
                    onChange={() => {
                      const s = new Set(transfer.brandIds.map(String));
                      const k = String(b.id);
                      if (s.has(k)) s.delete(k);
                      else s.add(k);
                      setTransfer({ ...transfer, brandIds: [...s] });
                    }}
                  />
                }
                label={b.brandName || b.clientName || b.id}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
          <Button variant="contained" color="secondary" onClick={submitTransfer}>
            Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Form;
